import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { RotateCcw } from 'lucide-react'
import {
  Alert,
  AdvancedDisclosure,
  Badge,
  Button,
  ConfirmDialog,
  Drawer,
  DropdownMenu,
  FormField,
  FormSection,
  Input,
  StatePanel,
  Switch,
  Toggle,
  ToggleGroup,
  Tooltip,
} from './common'
import BrandedFeedPreview from './BrandedFeedPreview'
import ContentSection from './post-creator/ContentSection'
import CtaSection from './post-creator/CtaSection'
import TagSelector from './TagSelector'
import { useOrganization } from '../contexts/OrganizationContext'
import { aiApi, feedOpsApi } from '../services/api'
import { suggestionToFeedPreviewItem } from '../utils/feedPreviewItem'
import styles from './PostInspectorDrawer.module.css'

const TITLE_TRUNCATE_LENGTH = 90

// CtaSection reports changes using its own camelCase field names; the drawer's
// draft (and the PATCH endpoint) use the snake_case body field names instead.
const CTA_FIELD_TO_DRAFT_KEY = {
  ctaKind: 'cta_kind',
  ctaLabel: 'cta_label',
  ctaTarget: 'cta_target',
}

function truncateText(value, maxLength) {
  const text = String(value || '').trim()
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1).trimEnd()}\u2026`
}

// Same pair of helpers as FeedOpsPage.jsx / PersonalizedBetsPage.jsx / CommandCenterPostCreator.jsx:
// datetime-local inputs round-trip through a local "YYYY-MM-DDTHH:mm" string, converted to/from ISO at the edges.
function formatDateTimeInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (part) => String(part).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toIsoOrNull(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
function formatDateTime(value) {
  if (!value) return '\u2014'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '\u2014' : date.toLocaleString()
}


function extractTagIds(tags) {
  if (!Array.isArray(tags)) return []
  return tags.map((tag) => tag?.id ?? tag?.tag_id).filter((id) => id !== undefined && id !== null)
}

function sameIdSet(a = [], b = []) {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((id) => setB.has(id))
}

function draftFieldEqual(key, a, b) {
  if (key === 'tag_ids') return sameIdSet(a, b)
  return a === b
}

// The overview family's `representative` only carries read-only summary fields today
// (see FeedOpsOverviewRepresentative in services/feed_ops_overview.go). Content-edit fields are
// read with safe fallbacks so the drawer degrades gracefully rather than crashing while
// that struct grows to carry them; see the summary note for the integration follow-up.
function buildDraftFromFamily(family) {
  const rep = family?.representative || {}
  return {
    text: rep.text ?? '',
    title: rep.title ?? '',
    subtitle: rep.subtitle ?? '',
    cta_kind: rep.cta_kind ?? 'none',
    cta_label: rep.cta_label ?? '',
    cta_target: rep.cta_target ?? '',
    tag_ids: extractTagIds(rep.tags),
    is_hidden: Boolean(rep.is_hidden),
    is_pinned: Boolean(rep.is_pinned),
    pinned_until: formatDateTimeInput(rep.pinned_until),
    manual_priority: rep.manual_priority ?? rep.boost_factor ?? 1,
  }
}

function buildSavePatch(draft, initial) {
  const patch = {}
  if (draft.text !== initial.text) patch.text = draft.text
  if (draft.title !== initial.title) patch.title = draft.title
  if (draft.subtitle !== initial.subtitle) patch.subtitle = draft.subtitle
  if (draft.cta_kind !== initial.cta_kind) patch.cta_kind = draft.cta_kind
  if (draft.cta_label !== initial.cta_label) patch.cta_label = draft.cta_label
  if (draft.cta_target !== initial.cta_target) patch.cta_target = draft.cta_target
  if (!sameIdSet(draft.tag_ids, initial.tag_ids)) patch.tag_ids = draft.tag_ids
  if (draft.is_hidden !== initial.is_hidden) patch.is_hidden = draft.is_hidden
  if (draft.is_pinned !== initial.is_pinned) patch.is_pinned = draft.is_pinned
  if (draft.pinned_until !== initial.pinned_until) patch.pinned_until = toIsoOrNull(draft.pinned_until)
  if (draft.manual_priority !== initial.manual_priority) patch.manual_priority = draft.manual_priority
  return patch
}

// Flattens family.variants into renderable rows, inserting one heading row above
// each run of *consecutive* variants that resolve to the same regulation label
// (e.g. two Pennsylvania offering codes) so the label isn't repeated per row.
function regulationRowsFromVariants(variants, regulationFor) {
  const labeled = variants.map((variant) => ({
    variant,
    // Empty means the provider code has no mapped regulation name. The code
    // then stands in as the row's identity instead of an "unknown" placeholder.
    label: regulationFor?.(variant.offering_id) || variant.regulation_label || '',
  }))

  const rows = []
  let index = 0
  while (index < labeled.length) {
    let end = index + 1
    while (end < labeled.length && labeled[index].label && labeled[end].label === labeled[index].label) end += 1
    const groupSize = end - index
    const groupKey = `${labeled[index].variant.post_id}-${labeled[index].variant.offering_id}`
    if (groupSize > 1) {
      rows.push({ kind: 'heading', key: `heading-${groupKey}`, label: labeled[index].label })
    }
    for (let cursor = index; cursor < end; cursor += 1) {
      const entry = labeled[cursor]
      rows.push({
        kind: 'variant',
        key: `${entry.variant.post_id}-${entry.variant.offering_id}`,
        variant: entry.variant,
        label: entry.label,
        grouped: groupSize > 1,
      })
    }
    index = end
  }
  return rows
}

function placementStatusLabel(variant) {
  if (variant.pin_to_top) return 'Pinned'
  if (variant.placement_strategy === 'top') return 'Top'
  if (variant.placement_strategy === 'middle') return 'Middle'
  if (variant.placement_strategy === 'bottom') return 'Bottom'
  if (variant.placement_strategy === 'random') return 'Randomized'
  if (typeof variant.manual_rank === 'number') return `Manual #${variant.manual_rank}`
  return 'Automatic'
}

// Order/placement writes are scoped to feed_order_overrides / feed_offering_overrides rows
// with has_order_override = TRUE, while is_hidden and boost_factor are read independently of
// that flag (see the scoped_order vs visibility_ov/boost_ov laterals in services/feed_ops_queue.go).
// An inline placement editor here would therefore either no-op or silently force a manual
// rank, so placement is read-only status text; the notice below points editors at the real control.
function familyHasOrderingOverride(variants) {
  return variants.some((variant) => (
    Boolean(variant.pin_to_top)
    || Boolean(variant.placement_strategy)
    || typeof variant.manual_rank === 'number'
  ))
}

function RegulationRow({ variant, label, grouped, regulationFor, onRegulationChange, onResetVariant, disabled = false }) {
  const offeringId = variant.offering_id || ''
  const context = label ? `${label} (${offeringId})` : offeringId
  const otherOfferingIds = (variant.target_offerings || []).filter((id) => id && id !== offeringId)
  // Forking is no longer a standalone action: a regulation gets its own copy by
  // being selected in "Apply changes to" and saved, so the only per-row content
  // action left is the inverse.
  const menuItems = variant.is_variant && !disabled
    ? [{ key: 'reset', label: 'Reset to shared copy', icon: RotateCcw, onSelect: () => onResetVariant?.(variant) }]
    : []

  return (
    <tr className={variant.is_hidden ? styles.rowHidden : undefined}>
      <td className={styles.regulationCell}>
        <div className={styles.regulationIdentity}>
          {grouped || !label ? (
            <code className={styles.offeringCodeStandalone}>{offeringId || '\u2014'}</code>
          ) : (
            <>
              <span className={styles.regulationLabel}>{label}</span>
              <code className={styles.offeringCode}>{offeringId || '\u2014'}</code>
            </>
          )}
          {variant.is_variant ? <Badge tone="info" compact>Custom copy</Badge> : null}
          {variant.shared_post ? (
            <Tooltip
              placement="right"
              content={otherOfferingIds.length
                ? `Also serves ${otherOfferingIds.map((id) => `${regulationFor?.(id) || id} (${id})`).join(', ')}`
                : 'Also serves other regulations'}
            >
              <Badge tone="neutral" compact>Shared post</Badge>
            </Tooltip>
          ) : null}
        </div>
      </td>
      <td className={styles.visibleCell}>
        <Switch
          checked={!variant.is_hidden}
          disabled={disabled}
          onChange={(checked) => onRegulationChange?.(variant, { is_hidden: !checked })}
          aria-label={`Visible in ${context}`}
        />
      </td>
      <td className={styles.boostCell}>
        <Input
          type="number"
          size="compact"
          min="0"
          step="0.1"
          value={variant.boost_factor ?? 1}
          disabled={disabled}
          onChange={(event) => {
            const parsed = parseFloat(event.target.value)
            onRegulationChange?.(variant, { boost_factor: Number.isNaN(parsed) ? 0 : parsed })
          }}
          aria-label={`Boost for ${context}`}
        />
      </td>
      <td className={styles.placementCell}>
        <span className={styles.placementStatus}>{placementStatusLabel(variant)}</span>
      </td>
      <td className={styles.actionsCell}>
        {menuItems.length ? <DropdownMenu triggerLabel={`More actions for ${context}`} items={menuItems} /> : null}
      </td>
    </tr>
  )
}
function GeneratedParlayInspector({
  family,
  branding,
  regulationFor,
  title,
  onTitleChange,
  titleAction,
  canMutate,
}) {
  const post = family.representative
  const parlay = post.parlay || {}
  const previewItem = useMemo(() => suggestionToFeedPreviewItem({
    ...post,
    title: title ?? post.title,
  }, { fallbackId: post.id }), [post, title])
  const readiness = parlay.evaluation_status === 'ready'

  return (
    <>
      <AdvancedDisclosure label="Preview" defaultOpen>
        <BrandedFeedPreview branding={branding} item={previewItem} chrome={false} initialMode="card" allowFullscreen title="Post preview" />
      </AdvancedDisclosure>

      <section className={styles.generatedSection} aria-labelledby="generated-parlay-title">
        <h3 id="generated-parlay-title">Title</h3>
        <FormField
          label="Card title"
          name="title"
          value={title ?? ''}
          onChange={(event) => onTitleChange?.(event.target.value)}
          maxLength={120}
          placeholder="e.g., Yankees three-leg power parlay"
          disabled={!canMutate}
        />
        {titleAction ? (
          <div className={styles.fieldActionRow}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={titleAction.onClick}
              disabled={titleAction.disabled || titleAction.pending}
            >
              {titleAction.pending ? 'Generating…' : 'Generate new title'}
            </Button>
            {titleAction.reason ? <span className={styles.fieldActionHint}>{titleAction.reason}</span> : null}
            {titleAction.error ? <span className={styles.fieldActionError} role="alert">{titleAction.error}</span> : null}
          </div>
        ) : null}
      </section>
      <section className={styles.generatedSection} aria-labelledby="generated-parlay-details">
        <h3 id="generated-parlay-details">Parlay details</h3>
        <dl className={styles.generatedMeta}>
          <div>
            <dt>Status</dt>
            <dd><Badge tone="warning">Awaiting review</Badge></dd>
          </div>
          <div>
            <dt>League</dt>
            <dd>{parlay.sport || '\u2014'}</dd>
          </div>
          <div>
            <dt>Creator</dt>
            <dd>{post.creator?.display_name || post.creator_name || '\u2014'}</dd>
          </div>
          <div>
            <dt>Regulation</dt>
            <dd>{regulationFor?.(parlay.offering_id) || parlay.offering_id || '\u2014'}</dd>
          </div>
          <div>
            <dt>Total odds</dt>
            <dd>{parlay.price_decimal ? `${parlay.price_decimal}\u00d7` : '\u2014'}</dd>
          </div>
          <div>
            <dt>Kambi validation</dt>
            <dd><Badge tone={readiness ? 'success' : 'danger'}>{readiness ? 'Valid' : 'Refresh required'}</Badge></dd>
          </div>
          <div>
            <dt>Provider valid until</dt>
            <dd>{formatDateTime(parlay.valid_until)}</dd>
          </div>
          <div>
            <dt>Batch</dt>
            <dd><code>{parlay.generation_run_id || '\u2014'}</code></dd>
          </div>
          <div>
            <dt>Revision</dt>
            <dd>{parlay.revision ?? '\u2014'}</dd>
          </div>
        </dl>
      </section>

      <section className={styles.generatedSection} aria-labelledby="generated-parlay-legs">
        <h3 id="generated-parlay-legs">Legs</h3>
        {parlay.legs?.length ? (
          <ol className={styles.generatedLegs}>
            {parlay.legs.map((leg, index) => (
              <li key={leg.outcome_id || `${leg.event_id}-${index}`}>
                <strong>{leg.outcome_label || leg.outcome_id}</strong>
                <span>{[leg.event_label, leg.market_label].filter(Boolean).join(' \u00b7 ')}</span>
              </li>
            ))}
          </ol>
        ) : <p className={styles.sectionNotice}>No leg details are available.</p>}
      </section>
    </>
  )
}


// The eight winning ticket tokens a card palette may override, in display
// order; the swatches show what the feed will paint.
const TICKET_PALETTE_SWATCHES = [
  ['headerBackground', 'Band'],
  ['bodyBackground', 'Body'],
  ['textColor', 'Text'],
  ['oddsColor', 'Odds'],
  ['headerIconColor', 'Icon'],
  ['dividerColor', 'Divider'],
  ['ctaBackground', 'Button'],
  ['ctaColor', 'Button text'],
]

function WinningTicketColorsSection({ orgSlug, postId, ticket, canMutate, onChanged }) {
  const [busy, setBusy] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const palette = ticket?.team_palette || null
  const tokens = palette?.tokens && typeof palette.tokens === 'object' ? palette.tokens : {}
  const source = ticket?.team_palette_source || ''

  const run = async (action) => {
    if (!orgSlug || !postId || busy) return
    setBusy(action)
    setNotice('')
    setError('')
    try {
      const result = action === 'match'
        ? await feedOpsApi.matchTicketColors(orgSlug, postId)
        : await feedOpsApi.resetTicketColors(orgSlug, postId)
      if (action === 'match' && result && result.resolved === false) {
        setNotice(result.reason || 'No single team resolves for this card.')
      } else {
        setNotice(action === 'match' ? 'Team colors applied. The feed updates on the next projection.' : 'Back to brand colors. The feed updates on the next projection.')
      }
      onChanged?.(result)
    } catch (failure) {
      setError(failure?.response?.data?.error || 'Team colors could not be changed. Try again.')
    } finally {
      setBusy('')
    }
  }

  return (
    <AdvancedDisclosure label="Team colors" defaultOpen={Boolean(palette)}>
      {palette ? (
        <div className={styles.ticketColors}>
          <p className={styles.sectionNotice}>
            {palette.team_name ? `Colored for ${palette.team_name}` : 'Colored for its team'}
            {source === 'operator' ? ' (set by an operator)' : source === 'auto' ? ' (set when the card published)' : ''}
          </p>
          <ul className={styles.ticketSwatches} aria-label="Team color tokens">
            {TICKET_PALETTE_SWATCHES.filter(([token]) => tokens[token]).map(([token, label]) => (
              <li key={token} title={`${token}: ${tokens[token]}`}>
                <span className={styles.ticketSwatch} style={{ backgroundColor: tokens[token] }} aria-hidden="true" />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={styles.sectionNotice}>This card uses the brand's winning ticket colors.</p>
      )}
      <div className={styles.ticketColorActions}>
        <Button type="button" variant="secondary" size="sm" disabled={!canMutate || Boolean(busy)} onClick={() => run('match')}>
          {busy === 'match' ? 'Matching…' : 'Match team colors'}
        </Button>
        {palette ? (
          <Button type="button" variant="ghost" size="sm" disabled={!canMutate || Boolean(busy)} onClick={() => run('reset')}>
            {busy === 'reset' ? 'Resetting…' : 'Reset to brand colors'}
          </Button>
        ) : null}
      </div>
      {notice ? <p className={styles.sectionNotice} role="status">{notice}</p> : null}
      {error ? <p className={styles.sectionNotice} role="alert">{error}</p> : null}
    </AdvancedDisclosure>
  )
}

export default function PostInspectorDrawer({
  open,
  onClose,
  family,
  branding,
  regulationFor,
  canEdit,
  // Shown when canEdit is false for a role reason, so the disabled controls
  // explain themselves instead of looking broken.
  readOnlyNote = '',
  // Per-regulation visibility, boost and reset use the feed-ops regulation
  // routes, which need Feed configure or operate.
  canChangeRegulations = true,
  saving,
  canApproveGenerated = false,
  publishingGenerated = false,
  publishGeneratedError = '',
  canDeleteGenerated = false,
  deletingGenerated = false,
  scope = '',
  scopeOptions = [],
  scopeLoading = false,
  editorPostId = null,
  editorLabel = 'Open full editor',
  // False when there is no editor this operator can actually reach — a
  // distributed post for someone without Command Center access. Rendering the
  // button anyway would leave a control that silently does nothing.
  editorAvailable = true,
  detailError = false,
  onRetryDetail,
  groupDestinations = null,
  groupLoading = false,
  groupError = false,
  onScopeChange,
  onSave,
  onRegulationChange,
  onResetVariant,
  onOpenFullEditor,
  onApproveGenerated,
  onDeleteGenerated,
  onTicketColorsChanged,
  inline = false,
}) {
  const { currentOrg } = useOrganization()
  const [draft, setDraft] = useState(() => buildDraftFromFamily(family))
  const [initialDraft, setInitialDraft] = useState(() => buildDraftFromFamily(family))
  const [confirmIntent, setConfirmIntent] = useState(null)
  const [generatingTitle, setGeneratingTitle] = useState(false)
  const [titleGenerationError, setTitleGenerationError] = useState('')
  const [avoidTitlesHistory, setAvoidTitlesHistory] = useState([])
  // Mirrored so the reset effect below can check dirtiness against the *latest*
  // draft/initialDraft without depending on them — depending on that state directly
  // would re-run the reset (and fight the operator) on every keystroke.
  const draftRef = useRef(draft)
  const initialRef = useRef(initialDraft)
  useEffect(() => { draftRef.current = draft }, [draft])
  useEffect(() => { initialRef.current = initialDraft }, [initialDraft])

  // Scope is part of the edited identity: switching it swaps every field in the
  // form for another post's content, so it must reset the draft exactly like
  // opening a different row does.
  const familyId = family ? `${family.root_post_id || family.representative?.id || ''}:${scope}` : null
  const familyIdRef = useRef(familyId)

  // FeedOpsPage fetches full post detail asynchronously after the drawer opens and
  // merges it into family.representative, so `family` gets a fresh object reference on
  // nearly every parent re-render — often with identical content, sometimes with
  // previously-missing edit fields (cta_kind/tags/is_hidden/...) filled in for the first
  // time. seed/seedKey re-derive the draft shape and its value identity (a plain string)
  // so the reset effect only reacts to genuine content changes, not reference churn.
  const seed = useMemo(() => buildDraftFromFamily(family), [family])
  const seedKey = useMemo(() => JSON.stringify(seed), [seed])

  useEffect(() => {
    const identityChanged = familyIdRef.current !== familyId
    familyIdRef.current = familyId
    setConfirmIntent(null)

    if (identityChanged) {
      // A genuinely different post: always reset fully regardless of dirty state —
      // the same guarantee the old identity-keyed effect gave.
      setInitialDraft(seed)
      setDraft(seed)
      return
    }

    // Same post — family only changed because the async detail fetch landed (or
    // re-landed identical data). The baseline always tracks server truth so Save
    // keeps diffing correctly; a clean draft can just adopt it outright. A dirty
    // draft must keep every field the operator has actually diverged on, but still
    // absorb the fresh values for every field they haven't touched — otherwise those
    // fields would sit on stale (often just-a-fallback-default) values and Save would
    // incorrectly "revert" them.
    const dirty = Object.keys(buildSavePatch(draftRef.current, initialRef.current)).length > 0
    setInitialDraft(seed)
    if (!dirty) {
      setDraft(seed)
      return
    }
    setDraft((currentDraft) => {
      const merged = { ...seed }
      for (const key of Object.keys(currentDraft)) {
        if (!draftFieldEqual(key, currentDraft[key], initialRef.current[key])) {
          merged[key] = currentDraft[key]
        }
      }
      return merged
    })
    // seedKey is the value identity of seed, and familyId is read through a ref:
    // depending on seed/family directly would re-run this effect (and fight the
    // operator's in-progress edit) on every parent re-render that merely rebuilds
    // the family object without changing its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey])

  const patch = useMemo(() => buildSavePatch(draft, initialDraft), [draft, initialDraft])
  const isDirty = Object.keys(patch).length > 0

  const regulationRows = useMemo(
    () => regulationRowsFromVariants(family?.variants || [], regulationFor),
    [family?.variants, regulationFor]
  )

  const hasOrderingOverride = useMemo(
    () => familyHasOrderingOverride(family?.variants || []),
    [family?.variants]
  )

  // Derived from the rows actually on screen rather than the family's summary
  // counter, so the notice can never contradict the table beside it.
  const hasForkedRegulation = useMemo(
    () => (family?.variants || []).some((variant) => variant.is_variant),
    [family?.variants]
  )

  // A long run of all-default regulations carries no per-row information, so
  // "Where it runs" leads with one summary line and tucks the editable table
  // behind a disclosure. Any hidden row, boost, placement, or custom copy
  // keeps the table open.
  const uniformRegulationRun = useMemo(() => {
    const variants = family?.variants || []
    return variants.length > 6 && variants.every((variant) => (
      !variant.is_variant
      && !variant.is_hidden
      && (variant.boost_factor ?? 1) === 1
      && !variant.pin_to_top
      && !variant.placement_strategy
      && typeof variant.manual_rank !== 'number'
    ))
  }, [family?.variants])

  const updateDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }))

  const handleCtaChange = (field, value) => updateDraft(CTA_FIELD_TO_DRAFT_KEY[field] || field, value)

  const handleBoostChange = (event) => {
    const parsed = parseFloat(event.target.value)
    updateDraft('manual_priority', Number.isNaN(parsed) ? 0 : parsed)
  }

  const requestClose = () => {
    if (isDirty) {
      setConfirmIntent({ type: 'close' })
      return
    }
    onClose?.()
  }

  const requestScopeChange = (nextScope) => {
    if (nextScope === scope) return
    if (isDirty) {
      setConfirmIntent({ type: 'scope', value: nextScope })
      return
    }
    onScopeChange?.(nextScope)
  }

  const confirmDiscard = () => {
    const intent = confirmIntent
    setDraft(initialDraft)
    setConfirmIntent(null)
    if (intent?.type === 'scope') {
      onScopeChange?.(intent.value)
      return
    }
    onClose?.()
  }

  const cancelDiscard = () => setConfirmIntent(null)

  // A detail-load failure means the fields on screen are still just the row's
  // list-summary snapshot, not the authoritative record; never let an
  // operator mutate from that stale surface, only retry.
  const canMutate = canEdit && !detailError

  const handleSave = () => {
    if (!isDirty || !canMutate) return
    onSave?.(patch)
  }

  const representative = family?.representative
  const isGeneratedParlay = representative?.parlay?.origin === 'generated'
  const isGeneratedDraft = isGeneratedParlay
    && !representative.is_published
    && representative.is_hidden
  const creatorName = isGeneratedParlay
    ? 'Generated parlay'
    : representative?.creator?.display_name || 'Unknown creator'
  const headline = representative?.title || representative?.text || ''
  const lifecycle = representative?.lifecycle || ''
  const lifecycleLabel = representative?.lifecycle_label || ''
  const lifecycleReason = representative?.lifecycle_reason || ''
  const archivedAt = representative?.archived_at || ''
  const betLegs = Array.isArray(representative?.bet_legs) ? representative.bet_legs : []

  const orgSlug = family?.representative?.organization_slug || family?.representative?.org_slug || currentOrg?.slug || ''
  const isPersonalized = Boolean(
    representative?.source_user_bet_id ||
    representative?.post_type === 'recommendation' ||
    representative?.content_origin === 'personalized'
  )
  const isPrePack = isGeneratedParlay || isPersonalized
  const isWinningTicket = representative?.post_type === 'winning_ticket'
    || representative?.content_origin === 'winning_ticket'
    || representative?.content_origin === 'creator_winning_ticket'

  const capabilitiesQuery = useQuery({
    queryKey: ['ai-capabilities', orgSlug],
    queryFn: () => aiApi.getCapabilities(orgSlug),
    enabled: Boolean(orgSlug && isPrePack),
    staleTime: 60000,
  })

  const prepackFeature = capabilitiesQuery.data?.features?.prepack_titles
  const isAIEnabled = Boolean(prepackFeature?.enabled)
  const disabledReason = prepackFeature?.enabled === false
    ? prepackFeature?.reason?.message || 'Title generation is disabled'
    : (!capabilitiesQuery.data?.configured ? 'AI is not configured for this organization' : null)

  const handleGenerateTitle = async () => {
    const targetId = editorPostId || family?.root_post_id || representative?.id
    if (!orgSlug || !targetId || generatingTitle) return
    setGeneratingTitle(true)
    setTitleGenerationError('')

    const currentTitle = String(draft.title || '').trim()
    const avoid = Array.from(new Set([
      currentTitle,
      ...avoidTitlesHistory,
    ])).filter(Boolean).slice(-4)

    try {
      const res = await aiApi.generatePrePackTitle(orgSlug, {
        target_kind: 'post',
        target_id: targetId,
        avoid_titles: avoid,
      })
      if (res?.title) {
        updateDraft('title', res.title)
        setAvoidTitlesHistory((prev) => [...prev, res.title])
        toast.success('Generated new title draft')
      }
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.error || 'Failed to generate title'
      setTitleGenerationError(msg)
    } finally {
      setGeneratingTitle(false)
    }
  }

  const titleAction = isPrePack ? {
    onClick: handleGenerateTitle,
    pending: generatingTitle,
    disabled: !isAIEnabled || !canMutate,
    reason: !isAIEnabled ? disabledReason : null,
    error: titleGenerationError,
  } : null

  const footer = isGeneratedParlay ? (
    <>
      <Button variant="ghost" onClick={requestClose}>Close</Button>
      {isGeneratedDraft && canDeleteGenerated ? (
        <Button
          variant="danger"
          loading={deletingGenerated}
          onClick={() => onDeleteGenerated?.(representative)}
        >
          Delete draft
        </Button>
      ) : null}
      {isDirty ? (
        <Button
          variant="secondary"
          loading={saving}
          disabled={!canMutate}
          onClick={handleSave}
        >
          Save title
        </Button>
      ) : null}
      {isGeneratedDraft ? (
        <>
          {!canApproveGenerated ? (
            <span className={styles.generatedApprovalNote}>An internal admin approves generated parlays.</span>
          ) : isDirty ? (
            <span className={styles.generatedApprovalNote}>Save the title before publishing.</span>
          ) : null}
          <Button
            variant="primary"
            loading={publishingGenerated}
            disabled={!canApproveGenerated || isDirty}
            onClick={() => onApproveGenerated?.(representative.id)}
          >
            Approve &amp; publish
          </Button>
        </>
      ) : null}
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={requestClose}>Cancel</Button>
      {editorAvailable ? (
        <Button
          variant="secondary"
          disabled={!family}
          onClick={() => family && onOpenFullEditor?.(editorPostId || family.root_post_id || representative?.id)}
        >
          {editorLabel}
        </Button>
      ) : null}
      <Button variant="primary" loading={saving} disabled={!isDirty || !canMutate} onClick={handleSave}>
        Save
      </Button>
    </>
  )

  const regulationTable = (
    <div className={styles.tableRegion}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Regulation</th>
            <th scope="col">Visible</th>
            <th scope="col">Boost</th>
            <th scope="col">Placement</th>
            <th scope="col"><span className={styles.visuallyHidden}>Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {regulationRows.length ? regulationRows.map((row) => (
            row.kind === 'heading' ? (
              <tr key={row.key} className={styles.groupHeadingRow}>
                <th scope="rowgroup" colSpan={5} className={styles.groupHeadingCell}>{row.label}</th>
              </tr>
            ) : (
              <RegulationRow
                key={row.key}
                variant={row.variant}
                label={row.label}
                grouped={row.grouped}
                regulationFor={regulationFor}
                onRegulationChange={onRegulationChange}
                onResetVariant={onResetVariant}
                disabled={!canChangeRegulations}
              />
            )
          )) : (
            <tr>
              <td colSpan={5} className={styles.emptyState}>No regulations are linked to this post yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <Drawer
        open={Boolean(open)}
        onClose={requestClose}
        side="right"
        title={creatorName}
        description={truncateText(headline, TITLE_TRUNCATE_LENGTH) || undefined}
        className={styles.surface}
        inline={inline}
        footer={footer}
      >
        {family ? (
          <div className={styles.body}>
            {isGeneratedParlay ? (
              <>
                <GeneratedParlayInspector
                  family={family}
                  branding={branding}
                  regulationFor={regulationFor}
                  title={draft.title}
                  onTitleChange={(value) => updateDraft('title', value)}
                  titleAction={titleAction}
                  canMutate={canMutate}
                />
                {publishGeneratedError ? <Alert variant="error">{publishGeneratedError}</Alert> : null}
              </>
            ) : (
              <>
            {(lifecycle && lifecycle !== 'active') || detailError || scopeLoading ? (
              <div className={styles.lifecycleRow}>
                {lifecycle && lifecycle !== 'active' ? (
                  <Badge tone={lifecycle === 'archived_settled' ? 'neutral' : 'warning'}>{lifecycleLabel || 'Archived'}</Badge>
                ) : null}
                {lifecycle && lifecycle !== 'active' && lifecycleReason ? (
                  <span className={styles.sectionNotice}>{lifecycleReason}</span>
                ) : null}
                {lifecycle && lifecycle !== 'active' && archivedAt ? (
                  <span className={styles.sectionNotice}>Archived {formatDateTime(archivedAt)}</span>
                ) : null}
              </div>
            ) : null}

            {!canEdit && readOnlyNote ? (
              <p className={styles.sectionNotice}>{readOnlyNote}</p>
            ) : null}

            {detailError ? (
              <StatePanel
                compact
                state="error"
                title="Post details could not load"
                description="Editing is unavailable until the authoritative record loads. Every field on screen is still the row summary, not the full record."
                onRetry={onRetryDetail}
              />
            ) : scopeLoading ? (
              <StatePanel compact state="loading" title="Loading post details" description="Editing is unavailable until the authoritative record loads." />
            ) : null}

            <AdvancedDisclosure label="Preview" defaultOpen>
              <BrandedFeedPreview branding={branding} item={family.representative} chrome={false} initialMode="card" allowFullscreen title="Post preview" />
            </AdvancedDisclosure>

            <AdvancedDisclosure label="Market" defaultOpen={betLegs.length > 0}>
              {betLegs.length ? (
                <ul className={styles.generatedLegs}>
                  {betLegs.map((leg) => (
                    <li key={leg.id || leg.selection_id}>
                      <strong>{leg.event_name || 'Event unavailable'}</strong>
                      <span>{[leg.market_name, leg.selection_name].filter(Boolean).join(' · ') || 'Market unavailable'}</span>
                      {leg.odds_american !== undefined && leg.odds_american !== null ? (
                        <span>{leg.odds_american}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.sectionNotice}>No bet is attached to this post.</p>
              )}
            </AdvancedDisclosure>

            {isWinningTicket ? (
              <WinningTicketColorsSection
                orgSlug={orgSlug}
                postId={representative?.id}
                ticket={representative?.winning_ticket}
                canMutate={canMutate}
                onChanged={onTicketColorsChanged}
              />
            ) : null}

            {groupDestinations || groupLoading || groupError ? (
              <AdvancedDisclosure label="Distributed post membership" defaultOpen>
                {groupLoading ? (
                  <StatePanel compact state="loading" title="Loading distributed post membership" />
                ) : groupError ? (
                  <StatePanel compact state="error" title="Membership could not load" description="The book/provider scope below may be incomplete." onRetry={onRetryDetail} />
                ) : (groupDestinations || []).length ? (
                  <ul className={styles.groupMemberList}>
                    {groupDestinations.map((destination) => (
                      <li key={destination.organization_id || destination.post_id} className={styles.groupMemberRow}>
                        <span>{destination.organization_name || destination.organization_slug || 'Book unavailable'}</span>
                        {destination.lifecycle ? <Badge compact tone={destination.lifecycle === 'active' ? 'success' : 'neutral'}>{destination.lifecycle.replaceAll('_', ' ')}</Badge> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.sectionNotice}>No other books are linked to this distributed post.</p>
                )}
              </AdvancedDisclosure>
            ) : null}

            {scopeOptions.length > 1 ? (
              <div className={styles.scopeBar}>
                <FormField
                  label="Apply changes to"
                  name="editScope"
                  type="select"
                  value={scope}
                  options={scopeOptions}
                  disabled={!canMutate}
                  onChange={(event) => requestScopeChange(event.target.value)}
                />
                {scope ? (
                  <p className={styles.sectionNotice}>
                    Saving updates this regulation only. Every other regulation keeps the shared copy.
                  </p>
                ) : null}
              </div>
            ) : null}

            <fieldset className={styles.fieldset} disabled={!canMutate || scopeLoading}>
              <ContentSection
                text={draft.text}
                onTextChange={(value) => updateDraft('text', value)}
                title={draft.title}
                subtitle={draft.subtitle}
                onTitleChange={(value) => updateDraft('title', value)}
                onSubtitleChange={(value) => updateDraft('subtitle', value)}
                titleAction={titleAction}
                creatorSlot={null}
                tagsSlot={<TagSelector selectedTagIds={draft.tag_ids} onChange={(ids) => updateDraft('tag_ids', ids)} />}
                showSectionTitle
                collapseMoreOptions
              />

              <CtaSection
                ctaKind={draft.cta_kind}
                ctaLabel={draft.cta_label}
                ctaTarget={draft.cta_target}
                onChange={handleCtaChange}
              />

              <FormSection title="Feed status" headingSize="subsection" contentClassName={styles.feedStatusGrid}>
                <ToggleGroup>
                  <Toggle
                    label="Visible"
                    checked={!draft.is_hidden}
                    onChange={(checked) => updateDraft('is_hidden', !checked)}
                  />
                  <Toggle
                    label="Pinned"
                    checked={draft.is_pinned}
                    onChange={(checked) => updateDraft('is_pinned', checked)}
                  />
                </ToggleGroup>
                {draft.is_pinned ? (
                  <FormField
                    label="Pinned until"
                    name="pinnedUntil"
                    type="datetime-local"
                    value={draft.pinned_until}
                    onChange={(event) => updateDraft('pinned_until', event.target.value)}
                    help="Optional end time for the pin."
                  />
                ) : null}
                <FormField
                  label="Algorithm boost"
                  name="manualPriority"
                  type="number"
                  value={draft.manual_priority}
                  onChange={handleBoostChange}
                  step="0.1"
                  min="0"
                  help="Multiplies the system score in Automatic order."
                />
              </FormSection>

              <FormSection title="Where it runs" headingSize="subsection" contentClassName={styles.regulationSectionContent}>
                {hasForkedRegulation && !scope ? (
                  <p className={styles.sectionNotice}>Regulations with their own copy are not changed by this save. Pick that regulation above to edit its copy.</p>
                ) : null}
                {uniformRegulationRun ? (
                  <>
                    <p className={styles.sectionNotice}>
                      {`Runs in ${(family?.variants || []).length} regulations with the shared copy — all visible, automatic order.`}
                    </p>
                    <AdvancedDisclosure label="All regulations">
                      {regulationTable}
                    </AdvancedDisclosure>
                  </>
                ) : regulationTable}
                {hasOrderingOverride ? (
                  <p className={styles.sectionNotice}>Order and placement are set by arranging that regulation's feed.</p>
                ) : null}
              </FormSection>
            </fieldset>
              </>
            )}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={Boolean(confirmIntent)}
        title="Discard unsaved changes?"
        message={confirmIntent?.type === 'scope'
          ? 'Switching regulations will discard your unsaved edits to this copy.'
          : 'You have unsaved edits to this post. Closing now will discard them.'}
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        isDestructive
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
    </>
  )
}
